import type { GridCell, Level } from "./types";
import type { InterdictionRequest } from "@jpjj/solver-core/interdict";
import { shortestPath } from "@jpjj/solver-core/interdict";

/** Pixel size of one cell in the SVG coordinate space. */
export const CELL = 46;

const k = (r: number, c: number) => r + "," + c;

/** The interdiction request for a level (base layout + budget). The player's or
 *  solver's chosen walls are passed separately as the `blocked` set. */
export function reqFor(level: Level): InterdictionRequest {
  return {
    rows: level.rows,
    cols: level.cols,
    baseWalls: level.baseWalls,
    start: level.start,
    goal: level.goal,
    budget: level.budget,
  };
}

/** The hero's shortest path under a given set of added walls (cells + length). */
export function pathUnder(level: Level, walls: GridCell[]): { length: number; path: GridCell[] } | null {
  return shortestPath(reqFor(level), walls);
}

export interface DrawOpts {
  /** Highlight class for placed walls: "you" (slate) or "opt" (gold). */
  kind?: "you" | "opt";
  /** Show the hero/treasure markers (default true). */
  markers?: boolean;
  /** How many of `path`'s segments to draw (for tracing animation). Default all. */
  pathSteps?: number;
  /** Mark which interior cells are still legal wall spots (board only). */
  interactive?: boolean;
}

/**
 * Render a labyrinth into `holder` (an element that will hold one <svg>): floor
 * tiles, base walls, the player/solver walls, the hero's glowing shortest path,
 * and the hero + treasure markers. Pure given (level, walls, path) — both the
 * live board and the reveal call it.
 */
export function drawMaze(
  holder: HTMLElement,
  level: Level,
  walls: GridCell[],
  path: GridCell[],
  opts: DrawOpts = {},
): void {
  const { kind = "you", markers = true, interactive = false } = opts;
  const W = level.cols * CELL;
  const H = level.rows * CELL;
  const baseSet = new Set(level.baseWalls.map(([r, c]) => k(r, c)));
  const startK = k(...level.start);
  const goalK = k(...level.goal);

  const cx = (c: number) => c * CELL + CELL / 2;
  const cy = (r: number) => r * CELL + CELL / 2;

  let s = `<svg viewBox="0 0 ${W} ${H}" class="maze-svg" role="img" aria-label="Labyrinth grid">`;

  // floor + base walls + interactive hints
  for (let r = 0; r < level.rows; r++) {
    for (let c = 0; c < level.cols; c++) {
      const x = c * CELL;
      const y = r * CELL;
      const cell = k(r, c);
      if (baseSet.has(cell)) {
        s += `<rect class="t t-rock" x="${x}" y="${y}" width="${CELL}" height="${CELL}"/>`;
      } else {
        const terminal = cell === startK || cell === goalK;
        const placeable = interactive && !terminal;
        const cls = "t t-floor" + (placeable ? " t-place" : "");
        s += `<rect class="${cls}" x="${x}" y="${y}" width="${CELL}" height="${CELL}" data-r="${r}" data-c="${c}"/>`;
      }
    }
  }

  // player / solver walls (drawn on top, as raised blocks)
  for (const [r, c] of walls) {
    if (baseSet.has(k(r, c))) continue;
    s += `<rect class="t-build ${kind}" x="${c * CELL + 2}" y="${r * CELL + 2}" width="${CELL - 4}" height="${CELL - 4}" rx="5"/>`;
  }

  // hero path (glowing polyline through cell centres)
  if (path.length >= 2) {
    const steps = opts.pathSteps ?? path.length - 1;
    const pts = path.slice(0, Math.max(2, steps + 1)).map(([r, c]) => `${cx(c)},${cy(r)}`).join(" ");
    s += `<polyline class="hero-path glow ${kind}" points="${pts}"/>`;
    s += `<polyline class="hero-path core ${kind}" points="${pts}"/>`;
  }

  // markers
  if (markers) {
    const [sr, sc] = level.start;
    const [gr, gc] = level.goal;
    s += `<g class="m-hero" transform="translate(${cx(sc)},${cy(sr)})"><circle r="${CELL * 0.3}"/><text y="6" text-anchor="middle">🦊</text></g>`;
    s += `<g class="m-goal" transform="translate(${cx(gc)},${cy(gr)})"><circle r="${CELL * 0.32}"/><text y="7" text-anchor="middle">💎</text></g>`;
  }

  s += "</svg>";
  holder.innerHTML = s;
}
